import { TicketFormData } from "./types";

/**
 * Validates if a string is a valid email address
 * @param email The email address to validate
 * @returns True if the email is valid, false otherwise
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates the ticket form data based on the ticket type and current step
 * @param data Form data to validate
 * @param selectedModule The currently selected module type
 * @param currentStep The current step in the wizard (0 = TicketTypes, 1 = Details, etc.)
 * @returns Validation result with status and error message if applicable
 */
export const validateTicketForm = (
  data: TicketFormData,
  selectedModule: string,
  currentStep = -1 // -1 means validate everything (for backward compatibility)
): { isValid: boolean; errorMessage: string | null } => {
  // Step 0: Check if a ticket type is selected (Ticket Types step)
  if (currentStep === 0 || currentStep === -1) {
    if (!data.ticketTypes || data.ticketTypes.length === 0) {
      return { isValid: false, errorMessage: "Please select a ticket type" };
    }
    
    // If we're only validating this step, return success
    if (currentStep === 0) {
      return { isValid: true, errorMessage: null };
    }
  }
  
  // Step 1: Validate fields in the Details step
  if (currentStep === 1 || currentStep === -1) {
    const selectedTicketType = data.ticketTypes && data.ticketTypes.length > 0 ? data.ticketTypes[0] : '';
    
    if (selectedModule === 'UAR') {
      // Validate based on specific ticket type
      if (selectedTicketType === 'HST') {
        // Check for missing user
        if (!data.UARFields?.selectedUser) {
          return { isValid: false, errorMessage: "Please select or enter a user" };
        }
        
        // Validate required fields for the user (whether manually entered or selected)
        if (!data.UARFields.selectedUser.displayName) {
          return { isValid: false, errorMessage: "User User Name is required" };
        }
        
        // Email validation for manually entered users
        if (!data.UARFields.selectedUser.mail) {
          return { isValid: false, errorMessage: "User email is required" };
        }
        
        // Validate email format
        if (data.UARFields.selectedUser.mail && !isValidEmail(data.UARFields.selectedUser.mail)) {
          return { isValid: false, errorMessage: "Please enter a valid email address for user" };
        }
        
        // Check for missing role
        if (!data.UARFields.selectedRole) {
          return { isValid: false, errorMessage: "Please select a role" };
        }
        
        // Check for missing locations
        if (!data.UARFields.locations || data.UARFields.locations.length === 0) {
          return { isValid: false, errorMessage: "Please select at least one facility" };
        }
      } else if (selectedTicketType === 'CPSI') {        
        // Check for new user
        if (!data.CPSIFields?.newUser) {
          return { isValid: false, errorMessage: "Please select or enter a new user" };
        }
        
        // Validate required fields for the new user
        if (!data.CPSIFields.newUser.displayName) {
          return { isValid: false, errorMessage: "New user User Name is required" };
        }
        
        // Email validation for new user
        if (!data.CPSIFields.newUser.mail) {
          return { isValid: false, errorMessage: "New user email is required" };
        }
        
        // Validate email format for new user
        if (data.CPSIFields.newUser.mail && !isValidEmail(data.CPSIFields.newUser.mail)) {
          return { isValid: false, errorMessage: "Please enter a valid email address for new user" };
        }
        
        // Check for missing user to copy
        if (!data.CPSIFields?.userToCopy) {
          return { isValid: false, errorMessage: "Please select or enter a user to copy permissions from" };
        }
        
        // Validate required fields for manually entered users
        if (!data.CPSIFields.userToCopy.displayName) {
          return { isValid: false, errorMessage: "User to copy User Name is required" };
        }
        
        // Email validation for manually entered users
        if (!data.CPSIFields.userToCopy.mail) {
          return { isValid: false, errorMessage: "User to copy email is required" };
        }
        
        // Validate email format
        if (data.CPSIFields.userToCopy.mail && !isValidEmail(data.CPSIFields.userToCopy.mail)) {
          return { isValid: false, errorMessage: "Please enter a valid email address for user to copy" };
        }
        
        // Check for missing locations
        if (!data.CPSIFields?.locations || data.CPSIFields.locations.length === 0) {
          return { isValid: false, errorMessage: "Please select at least one facility" };
        }
      } else {
        // Generic UAR validation if ticket type is not HST or CPSI
        // Check for missing user
        if (!data.UARFields?.selectedUser) {
          return { isValid: false, errorMessage: "Please select or enter a user" };
        }
        
        // Validate required fields for the user (whether manually entered or selected)
        if (!data.UARFields.selectedUser.displayName) {
          return { isValid: false, errorMessage: "User User Name is required" };
        }
        
        // Email validation for manually entered users
        if (!data.UARFields.selectedUser.mail) {
          return { isValid: false, errorMessage: "User email is required" };
        }
        
        // Validate email format
        if (data.UARFields.selectedUser.mail && !isValidEmail(data.UARFields.selectedUser.mail)) {
          return { isValid: false, errorMessage: "Please enter a valid email address for user" };
        }
        
        // Check for missing role
        if (!data.UARFields.selectedRole) {
          return { isValid: false, errorMessage: "Please select a role" };
        }
      }
      
      // Check for missing due date (common for all UAR ticket types)
      if (!data.dueDate) {
        return { isValid: false, errorMessage: "Please select a due date" };
      }
    } else if (selectedModule === 'CPSI') {      
      // Check for new user
      if (!data.CPSIFields?.newUser) {
        return { isValid: false, errorMessage: "Please select or enter a new user" };
      }
      
      // Validate required fields for the new user
      if (!data.CPSIFields.newUser.displayName) {
        return { isValid: false, errorMessage: "New user User Name is required" };
      }
      
      // Email validation for new user
      // if (!data.CPSIFields.newUser.mail) {
      //   return { isValid: false, errorMessage: "New user email is required" };
      // }
      
      // Validate email format for new user
      // if (data.CPSIFields.newUser.mail && !isValidEmail(data.CPSIFields.newUser.mail)) {
      //   return { isValid: false, errorMessage: "Please enter a valid email address for new user" };
      // }
      
      // Check for missing user to copy
      if (!data.CPSIFields?.userToCopy) {
        return { isValid: false, errorMessage: "Please select or enter a user to copy permissions from" };
      }
      
      // Validate required fields for manually entered users
      if (!data.CPSIFields.userToCopy.displayName) {
        return { isValid: false, errorMessage: "User to copy User Name is required" };
      }
      
      // Email validation for manually entered users
      if (!data.CPSIFields.userToCopy.mail) {
        return { isValid: false, errorMessage: "User to copy email is required" };
      }
      
      // Validate email format
      if (data.CPSIFields.userToCopy.mail && !isValidEmail(data.CPSIFields.userToCopy.mail)) {
        return { isValid: false, errorMessage: "Please enter a valid email address for user to copy" };
      }
      
      // Check for missing due date
      if (!data.dueDate) {
        return { isValid: false, errorMessage: "Please select a due date" };
      }
    } else {
      // Check other ticket type requirements
      if (!data.title) {
        return { isValid: false, errorMessage: "Please enter a title" };
      }
      if (!data.dueDate) {
        return { isValid: false, errorMessage: "Please select a due date" };
      }
    }
    
    // If we're only validating this step, return success
    if (currentStep === 1) {
      return { isValid: true, errorMessage: null };
    }
  }
  
  // If all validations pass
  return { isValid: true, errorMessage: null };
};